import { OccupancyUtilisationLevels } from "@tether-web-portal/web-workers/occupancy/types/OccupancyUtilisation";
import {
  OCCUPANCY_EMPTY,
  OCCUPANCY_OPTIMAL_THRESHOLD,
  getUtilisationLevelColor,
} from "@tether-web-portal/components/occupancy/occupancy.colors";

import { Fraction01 } from "./units";

/**
 * Canonical utilisation-level bucketer.
 *
 * Rules (agreed 2026-04-21 during the occupancy refactor):
 *   - null or < 0              → LOW   (missing data is treated as underused;
 *                                       see bug M6 / Q3 decision)
 *   - [0, OPTIMAL_THRESHOLD)   → LOW
 *   - [OPTIMAL_THRESHOLD, 1]   → OPTIMAL
 *   - > 1                      → OVER  (over-capacity)
 *
 * `OPTIMAL_THRESHOLD` is exported from occupancy.colors.ts (currently 0.4).
 * At exactly the threshold, the level is OPTIMAL (>= comparison) — this
 * resolves the old boundary disagreement where the status badge said LOW
 * at 40% but the utilisation table said OPTIMAL.
 */
export const getUtilisationLevel = (
  fraction: Fraction01 | null,
): OccupancyUtilisationLevels => {
  if (fraction === null || fraction < 0) return OccupancyUtilisationLevels.LOW;
  if (fraction > 1) return OccupancyUtilisationLevels.OVER;
  if (fraction >= OCCUPANCY_OPTIMAL_THRESHOLD) return OccupancyUtilisationLevels.OPTIMAL;
  return OccupancyUtilisationLevels.LOW;
};

/**
 * Convenience for chart bar fills: maps a fraction directly to a colour.
 * Consumers that want a "too small to display" grey for near-zero bars
 * should use {@link getBarFillWithEmptyThreshold}.
 */
export const getUtilisationColor = (fraction: Fraction01 | null): string =>
  getUtilisationLevelColor(getUtilisationLevel(fraction));

/**
 * Same as {@link getUtilisationColor} but returns the "empty" grey when the
 * fraction is below the supplied threshold. Used by the Peak Days / Peak
 * Hours bar charts to wash out insignificant bars.
 */
export const getBarFillWithEmptyThreshold = (
  fraction: Fraction01 | null,
  emptyThreshold: Fraction01,
): string => {
  if (fraction === null || fraction <= emptyThreshold) return OCCUPANCY_EMPTY;
  return getUtilisationColor(fraction);
};
